import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQueryWithAuth } from '../../utils/baseQuery';

export interface ReportFile {
  fileName: string;
  createdDate?: string;
  size?: number;
  [key: string]: any;
}

export interface UploadHistoryItem {
  id: string;
  userId: string;
  userName: string;
  fileName: string;
  fileInfo: string;
  createdDate: string;
  uploadStatus: string;
}

export interface PagingInfo {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface UploadHistoryPaging {
  limit: number;
  count: number;
  offset: number;
  total: number;
}

export interface ReportsResponse {
  results: ReportFile[];
  paging: PagingInfo;
}

export interface UploadHistoryResponse {
  results: UploadHistoryItem[];
  paging: UploadHistoryPaging;
}

export interface ReportsQueryParams {
  page?: number;
  pageSize?: number;
}

export interface UploadHistoryQueryParams {
  limit?: number;
  offset?: number;
}

export const reportsApi = createApi({
  reducerPath: 'reportsApi',
  baseQuery: createBaseQueryWithAuth(`${process.env.REACT_APP_API_URL}/api/channel-partner-admin`),
  endpoints: (builder) => ({
    getReports: builder.query<ReportsResponse, ReportsQueryParams | void>({
      query: (params) => {
        const { page = 1, pageSize = 10 } = params || {};
        return {
          url: '/reports',
          params: { page, pageSize },
        };
      },
      transformResponse: (response: any): ReportsResponse => {
        
        if (response && response.results && response.paging) {
          return {
            results: Array.isArray(response.results) ? response.results : [],
            paging: response.paging
          };
        }
        
        let files: ReportFile[] = [];
        
        if (Array.isArray(response)) {
          files = response;
        } else if (response && typeof response === 'object') {
          if (response.files && Array.isArray(response.files)) {
            files = response.files;
          } else if (response.data && Array.isArray(response.data)) {
            files = response.data;
          } else if (typeof response === 'object') {
            files = Object.keys(response).map(fileName => ({
              fileName,
              ...response[fileName]
            }));
          }
        }
        
        return {
          results: files,
          paging: {
            page: 1,
            pageSize: files.length,
            total: files.length,
            totalPages: 1
          }
        };
      },
    }),
    downloadReport: builder.mutation<any, string>({
      query: (fileName) => ({
        url: `/reports/${fileName}`,
        method: 'GET',
      }),
    }),
    uploadParticipationReport: builder.mutation<any, File>({
      query: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: '/upload',
          method: 'POST',
          body: formData,
        };
      },
      extraOptions: {
        bypassChecksum: true,
      },
    }),
    uploadACHVoucher: builder.mutation<any, File>({
      query: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: '/upload-file',
          method: 'POST',
          body: formData,
        };
      },
      extraOptions: {
        bypassChecksum: true,
      },
    }),
    getUploadHistory: builder.query<UploadHistoryResponse, UploadHistoryQueryParams | void>({
      query: (params) => {
        const { limit = 10, offset = 0 } = params || {};
        return {
          url: '/upload-history',
          params: { limit, offset },
        };
      },
    }),
  }),
});

export const { 
  useGetReportsQuery, 
  useDownloadReportMutation,
  useUploadParticipationReportMutation,
  useUploadACHVoucherMutation,
  useGetUploadHistoryQuery
} = reportsApi; 